import { Reducer } from 'redux';
import {
    StreamingWidgetState,
    WidgetStreamState,
} from 'widgets/store/stream/WidgetStreamState';
import {
    createClearStreamDataAction,
    createReceiveMessageAction,
    createReceiveMessageBatchAction,
    WidgetClearStreamDataAction,
    WidgetClearStreamDataPayload,
    WidgetStreamAction,
    WidgetStreamMessageReceiveAction,
    WidgetStreamMessageReceiveBatchAction,
    WidgetStreamMessageReceivePayload,
} from 'widgets/store/stream/actions';
import State from 'store/State';
import { WidgetType } from 'widgets/WidgetType';
import {
    createProcessStreamDataMessage,
    createStreamReducer,
} from 'widgets/store/stream/reducer';
import { useSelector } from 'react-redux';
import { StreamElementDataMessage } from 'communication/types/StreamElement';
import { useWidgetContext } from 'widgets/WidgetContext';

export interface StreamSliceSelectors<TStreamState> {
    selectStreamState: (
        state: State,
        widgetKey: string
    ) => WidgetStreamState<TStreamState> | undefined;
    selectStreamKey: (state: State, widgetKey: string) => string | undefined;
    selectStreamData: (
        state: State,
        widgetKey: string
    ) => TStreamState | undefined;
    useSelectStreamData: () => TStreamState | undefined;
}

export interface StreamSliceActions<TDataStreamElement> {
    createReceiveMessageAction: (
        widgetKey: string,
        streamKey: string,
        message: TDataStreamElement,
        allowMultiStream?: boolean
    ) => WidgetStreamMessageReceiveAction<TDataStreamElement>;
    createReceiveMessageBatchAction: (
        widgetKey: string,
        streamKey: string,
        messages: TDataStreamElement[],
        allowMultiStream?: boolean
    ) => WidgetStreamMessageReceiveBatchAction<TDataStreamElement>;
    createClearStreamDataAction: (
        widgetKey: string
    ) => WidgetClearStreamDataAction;
}

export interface StreamSlice<TStreamState, TDataStreamElement> {
    selectors: StreamSliceSelectors<TStreamState>;
    actions: StreamSliceActions<TDataStreamElement>;
    reducer: Reducer<
        WidgetStreamState<TStreamState>,
        WidgetStreamAction<TDataStreamElement>
    >;
    widgetType: WidgetType;
}

export function buildStreamSlice<
    TWidgetDataState,
    TDataElement,
    TDataStreamElement,
>(
    widgetType: WidgetType,
    selectWidgetState: (
        state: State,
        widgetKey: string
    ) => StreamingWidgetState<TWidgetDataState>,
    initialState: WidgetStreamState<TWidgetDataState>,
    processDataStreamElement: (
        streamDataElement: TDataElement,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void,
    additionalReducer: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void = () => {
        return;
    }
): StreamSlice<TWidgetDataState, TDataStreamElement> {
    // Selectors...

    const selectStreamState = (state: State, widgetKey: string) => {
        const widgetState = selectWidgetState(state, widgetKey);
        return widgetState?.stream;
    };

    const selectStreamKey = (state: State, widgetKey: string) => {
        const streamState = selectStreamState(state, widgetKey);
        return streamState?.streamKey;
    };

    const selectStreamData = (state: State, widgetKey: string) => {
        const streamState = selectStreamState(state, widgetKey);
        return streamState?.data;
    };

    const useSelectStreamData = () => {
        const { widgetKey } = useWidgetContext();
        return useSelector((state: State) =>
            selectStreamData(state, widgetKey)
        );
    };

    // Actions...

    const createWidgetReceiveStreamMessageAction =
        createReceiveMessageAction<TDataStreamElement>(widgetType);

    const createWidgetReceiveStreamMessageBatchAction =
        createReceiveMessageBatchAction<TDataStreamElement>(widgetType);

    const createWidgetClearStreamDataAction =
        createClearStreamDataAction(widgetType);

    // Reducer...

    const reducer = createStreamReducer<TWidgetDataState, TDataStreamElement>(
        initialState,
        createProcessStreamDataMessage(
            processDataStreamElement,
            additionalReducer
            //TODO: fix types
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as any
    );

    return {
        selectors: {
            selectStreamState,
            selectStreamKey,
            selectStreamData,
            useSelectStreamData,
        },
        actions: {
            createReceiveMessageAction: createWidgetReceiveStreamMessageAction,
            createReceiveMessageBatchAction:
                createWidgetReceiveStreamMessageBatchAction,
            createClearStreamDataAction: createWidgetClearStreamDataAction,
        },
        reducer,
        widgetType,
    };
}

export type WidgetStreamSliceActionPayloadType<TDataStreamElement> =
    | WidgetStreamMessageReceivePayload<TDataStreamElement>
    | WidgetClearStreamDataPayload;

export function useStreamSlice<
    TWidgetDataState,
    TDataElement,
    TDataStreamElement extends StreamElementDataMessage<TDataElement>,
>(
    widgetType: WidgetType,
    selectWidgetState: (
        state: State,
        widgetKey: string
    ) => StreamingWidgetState<TWidgetDataState>,
    initialState: WidgetStreamState<TWidgetDataState>,
    processDataStreamElement: (
        streamDataElement: TDataElement,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void,
    additionalReducer: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void = () => {
        return;
    }
) {
    const { selectors } = buildStreamSlice<
        TWidgetDataState,
        TDataElement,
        TDataStreamElement
    >(
        widgetType,
        selectWidgetState,
        initialState,
        processDataStreamElement,
        additionalReducer
    );

    return {
        streamData: selectors.useSelectStreamData(),
    };
}
